import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ReportJobResponse {
  jobId: string;
  status: string;
}

export interface ReportJobStatus {
  id: string;
  reportType: string;
  status: string;
  fileName: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ReportJobsService {
  private baseUrl = `${environment.apiUrl}/ReportJobs`;
  private wompiPaymentsUrl = `${environment.apiUrl}/WompiPaymentsReports`;
  private paymentLinksAuditUrl = `${environment.apiUrl}/PaymentLinksAuditReports`;
  private portfolioUrl = `${environment.apiUrl}/reports/portfolio`;
  private punishedPortfolioUrl = `${environment.apiUrl}/PunishedPortfolioReport`;
  private budgetExecutionUrl = `${environment.apiUrl}/ProjectBudgetExecutionReports`;
  private expenseReceiptUrl = `${environment.apiUrl}/ExpenseReceiptReports`;
  private workOrdersUrl = `${environment.apiUrl}/WorkOrdersReports`;
  private projectProgramDetailUrl = `${environment.apiUrl}/ProjectProgramDetailReports`;
  private banksUrl = `${environment.apiUrl}/Banks`;
  private bankReconciliationUrl = `${environment.apiUrl}/BankReconciliation`;
  private wompiConsolidatedUrl = `${environment.apiUrl}/WompiReports`;
  private createdProjectsUrl = `${environment.apiUrl}/reports/created-projects`;
  private registeredDataUrl = `${environment.apiUrl}/reports/registered-data`;
  private participantsByIncpUrl = `${environment.apiUrl}/reports/participants-by-incp`;
  private projectFinancialStatementUrl = `${environment.apiUrl}/reports/project-financial-statement`;
  private projectEnrollmentsUrl = `${environment.apiUrl}/ProjectEnrollmentsReports`;

  constructor(private http: HttpClient) {}

  requestReport(reportType: string, parameters: any): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(`${this.baseUrl}/request`, {
      reportType,
      parametersJson: JSON.stringify(parameters)
    });
  }

  /**
   * Encola el reporte de pagos Wompi (comprobantes de ingreso pagados a través de la pasarela).
   * Usa Hangfire + SignalR (misma infraestructura que los otros reportes asíncronos).
   */
  queueWompiReport(from?: Date | string | null, to?: Date | string | null): Observable<ReportJobResponse> {
    const body: any = {};
    if (from) body.from = typeof from === 'string' ? from : from.toISOString();
    if (to) body.to = typeof to === 'string' ? to : to.toISOString();
    return this.http.post<ReportJobResponse>(`${this.wompiPaymentsUrl}/request`, body);
  }

  /**
   * Encola el reporte de Auditoría de Links de Pago (parte de PaymentLinks y los
   * cruza con IncomeReceipts para clasificarlos como Recaudado / Pendiente).
   * Filtra por GeneratedDate. Hangfire + SignalR.
   */
  queuePaymentLinksAuditReport(from?: Date | string | null, to?: Date | string | null): Observable<ReportJobResponse> {
    const body: any = {};
    if (from) body.from = typeof from === 'string' ? from : from.toISOString();
    if (to) body.to = typeof to === 'string' ? to : to.toISOString();
    return this.http.post<ReportJobResponse>(`${this.paymentLinksAuditUrl}/request`, body);
  }

  /**
   * Encola el reporte de cartera (Hangfire + SignalR).
   */
  queuePortfolioReport(filters: {
    startDate?: Date | string | null;
    endDate?: Date | string | null;
    projectId?: number | null;
    participantId?: number | null;
    companyId?: number | null;
  }): Observable<ReportJobResponse> {
    const body: any = {
      projectId: filters.projectId ?? null,
      participantId: filters.participantId ?? null,
      companyId: filters.companyId ?? null
    };
    if (filters.startDate) {
      body.startDate = typeof filters.startDate === 'string' ? filters.startDate : filters.startDate.toISOString();
    }
    if (filters.endDate) {
      body.endDate = typeof filters.endDate === 'string' ? filters.endDate : filters.endDate.toISOString();
    }
    return this.http.post<ReportJobResponse>(`${this.portfolioUrl}/request`, body);
  }

  /**
   * Encola el reporte de Cartera Castigada (Hangfire + SignalR).
   */
  queuePunishedPortfolioReport(filters: {
    startDate?: Date | string | null;
    endDate?: Date | string | null;
    projectId?: number | null;
    participantId?: number | null;
    companyId?: number | null;
  }): Observable<ReportJobResponse> {
    const body: any = {
      projectId: filters.projectId ?? null,
      participantId: filters.participantId ?? null,
      companyId: filters.companyId ?? null
    };
    if (filters.startDate) {
      body.startDate = typeof filters.startDate === 'string' ? filters.startDate : filters.startDate.toISOString();
    }
    if (filters.endDate) {
      body.endDate = typeof filters.endDate === 'string' ? filters.endDate : filters.endDate.toISOString();
    }
    return this.http.post<ReportJobResponse>(`${this.punishedPortfolioUrl}/request`, body);
  }

  /**
   * Encola el reporte de Ejecución Presupuestal (Hangfire + SignalR).
   */
  queueBudgetExecutionReport(projectId: number): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(`${this.budgetExecutionUrl}/request`, { projectId });
  }


  /**
   * Encola el reporte de Comprobantes de Egreso (Hangfire + SignalR).
   */
  queueExpenseReceiptReport(filters: {
    startDate: Date | string;
    endDate: Date | string;
    bankId?: number | null;
    thirdPartyId?: number | null;
    projectId?: number | null;
    dateType?: string | null;
    sortBy?: string | null;
    sortDirection?: string | null;
  }): Observable<ReportJobResponse> {
    const body = {
      startDate: typeof filters.startDate === 'string' ? filters.startDate : filters.startDate.toISOString(),
      endDate: typeof filters.endDate === 'string' ? filters.endDate : filters.endDate.toISOString(),
      bankId: filters.bankId ?? null,
      thirdPartyId: filters.thirdPartyId ?? null,
      projectId: filters.projectId ?? null,
      dateType: filters.dateType ?? 'Creation',
      sortBy: filters.sortBy ?? 'Id',
      sortDirection: filters.sortDirection ?? 'asc'
    };
    return this.http.post<ReportJobResponse>(`${this.expenseReceiptUrl}/request`, body);
  }

  /**
   * Encola el reporte de Órdenes de Trabajo (Hangfire + SignalR).
   */
  queueWorkOrdersReport(filters: {
    startDate?: Date | string | null;
    endDate?: Date | string | null;
    projectIds?: number[] | null;
    thirdPartyId?: number | null;
    payerId?: string | null;
    status?: string | null;
    includeCancelled?: boolean | null;
  }): Observable<ReportJobResponse> {
    const body: any = {
      projectIds: filters.projectIds ?? null,
      thirdPartyId: filters.thirdPartyId ?? null,
      payerId: filters.payerId ?? null,
      status: filters.status ?? null,
      includeCancelled: filters.includeCancelled ?? false
    };
    if (filters.startDate) {
      body.startDate = typeof filters.startDate === 'string' ? filters.startDate : filters.startDate.toISOString();
    }
    if (filters.endDate) {
      body.endDate = typeof filters.endDate === 'string' ? filters.endDate : filters.endDate.toISOString();
    }
    return this.http.post<ReportJobResponse>(`${this.workOrdersUrl}/request`, body);
  }

  /**
   * Encola el reporte de Detalle Programa Proyecto (Hangfire + SignalR).
   */
  queueProjectProgramDetailReport(projectId: number): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(`${this.projectProgramDetailUrl}/request`, { projectId });
  }

  /**
   * Encola el reporte bancario mensual (Hangfire + SignalR).
   */
  queueBankExcelReport(request: { month: number; year: number; bankId: number; dateType?: string; initialBalanceYear?: number | null; participantId?: number | null; }): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(`${this.banksUrl}/excel-report/request`, request);
  }

  /**
   * Encola el reporte bancario multi-mes (Hangfire + SignalR).
   */
  queueBankExcelReportMultiMonth(request: { months: number[]; year: number; bankId: number; dateType?: string; initialBalanceYear?: number | null; participantId?: number | null; }): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(`${this.banksUrl}/excel-report-multi-month/request`, request);
  }

  /**
   * Encola el reporte de saldos bancarios (Hangfire + SignalR).
   */
  queueBankBalanceReport(request: { month: number; year: number; bankId: number | null; dateType?: string; initialBalanceYear?: number | null; participantId?: number | null; }): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(`${this.banksUrl}/balance-excel-report/request`, request);
  }

  /**
   * Encola el reporte de conciliación bancaria por ID (Hangfire + SignalR).
   */
  queueBankReconciliationReportById(reconciliationId: number): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(`${this.bankReconciliationUrl}/${reconciliationId}/report/request`, {});
  }

  /**
   * Encola el reporte consolidado de Wompi (Hangfire + SignalR).
   */
  queueWompiConsolidatedReport(from?: Date | string | null, to?: Date | string | null): Observable<ReportJobResponse> {
    const body: any = {};
    if (from) body.from = typeof from === 'string' ? from : from.toISOString();
    if (to) body.to = typeof to === 'string' ? to : to.toISOString();
    return this.http.post<ReportJobResponse>(`${this.wompiConsolidatedUrl}/excel-consolidated/request`, body);
  }

  /**
   * Encola el reporte de Proyectos Creados (Hangfire + SignalR).
   */
  queueCreatedProjects(filters: {
    startDate?: Date | null;
    endDate?: Date | null;
    projectId?: number | null;
  }): Observable<ReportJobResponse> {
    const body: any = {};
    if (filters.startDate) body.startDate = filters.startDate.toISOString();
    if (filters.endDate)   body.endDate   = filters.endDate.toISOString();
    if (filters.projectId) body.projectId = filters.projectId;

    return this.http.post<ReportJobResponse>(
      `${this.createdProjectsUrl}`,
      body
    );
  }

  /**
   * Encola el reporte de Participantes por INCP (Hangfire + SignalR).
   */
  queueParticipantsByIncp(filters: {
    startDate?: Date | null;
    endDate?: Date | null;
    projectId?: number | null;
  }): Observable<ReportJobResponse> {
    const body: any = {};
    if (filters.startDate) body.startDate = filters.startDate.toISOString();
    if (filters.endDate)   body.endDate   = filters.endDate.toISOString();
    if (filters.projectId) body.projectId = filters.projectId;
    return this.http.post<ReportJobResponse>(this.participantsByIncpUrl, body);
  }

  /**
   * Encola el reporte de Inscripciones por Proyecto (Hangfire + SignalR).
   */
  queueProjectEnrollmentsReport(filters: {
    projectId?: number | null;
    participantId?: number | null;
    companyId?: number | null;
    period?: string | null;
  }): Observable<ReportJobResponse> {
    const body = {
      projectId: filters.projectId ?? null,
      participantId: filters.participantId ?? null,
      companyId: filters.companyId ?? null,
      period: filters.period ?? null
    };
    return this.http.post<ReportJobResponse>(`${this.projectEnrollmentsUrl}/request`, body);
  }

  queueProjectFinancialStatement(projectId: number): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(this.projectFinancialStatementUrl, { projectId });
  }

  /**
   * Encola el reporte de Datos Registrados (proveedores/empresas/estudiantes).
   */
  queueRegisteredData(): Observable<ReportJobResponse> {
    return this.http.post<ReportJobResponse>(
      `${this.registeredDataUrl}`,
      {}
    );
  }

  getJobStatus(jobId: string): Observable<ReportJobStatus> {
    return this.http.get<ReportJobStatus>(`${this.baseUrl}/status/${jobId}`);
  }

  downloadReport(jobId: string, fileName?: string): void {
    this.http.get(`${this.baseUrl}/download/${jobId}`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'reporte.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading report:', error);
      }
    });
  }

  /**
   * Obtiene el blob del reporte sin disparar la descarga (útil para preview).
   */
  fetchReportBlob(jobId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/download/${jobId}`, { responseType: 'blob' });
  }

  getMyJobs(): Observable<ReportJobStatus[]> {
    return this.http.get<ReportJobStatus[]>(`${this.baseUrl}/my-jobs`);
  }
}
